"use client";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import ProductRecommendations from "@components/commerce/product/recommendations/DeferredProductRecommendations";
import type { ILinksCollection } from "@contentful-api/types/contentful";
import { AlgoliaRecommendModel, type RecommendationResult } from "@lib/algolia/types";
import { CommonCMS } from "@lib/constants/contentful";
import { PRODUCT_CAROUSEL_DEFAULT_TILE_WIDTHS } from "@lib/constants/carousel";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useRouter } from "@i18n/routing";
import { H } from "@ecco/ui/a11y-heading";
import { Text } from "@ecco/ui/text";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { useSmBreakpoint } from "@ecco/ui/use-breakpoint";
import { useLocale } from "next-intl";
import type { FC } from "react";
import SearchCMS from "./cms-search";
import { didYouMeanHeadingStyles, suggestionLinkStyles } from "./EmptyResultSearchPage.styles";
import { SearchCarousels } from "./SearchCarousels";
import { useDidYouMeanSuggestion } from "./useDidYouMeanSuggestion";

interface EmptyResultSearchPageProps {
    /** Search term for the no-results headline / did-you-mean. Unused when showQueryFeedback is false. */
    query?: string;
    trendingRecommendations: RecommendationResult[];
    customLinksCarousel?: ILinksCollection[];
    /** When false (empty search page), hide headline and did-you-mean. Defaults to true. */
    showQueryFeedback?: boolean;
}

/** One continuous underlined link for “{{suggestion}}” ({{count}}) without CMS changes. */
const renderDidYouMean = (
    template: string,
    suggestion: { term: string; count?: number },
    onClick: () => void
) => {
    // Capture optional quotes around suggestion, text between placeholders, then count
    const match = template.match(/([“"']?)\{\{suggestion\}\}([”"']?)(.*?)\{\{count\}\}/);
    if (!match || match.index == null) {
        return template
            .replaceAll("{{suggestion}}", suggestion.term)
            .replaceAll("{{count}}", String(suggestion.count ?? ""));
    }

    const [, openQuote, closeQuote, middle] = match;
    let after = template.slice(match.index + match[0].length);
    let trailing = "";
    if (after.startsWith(")")) {
        trailing = ")";
        after = after.slice(1);
    }

    const label = `${openQuote}${suggestion.term}${closeQuote}${middle}${suggestion.count ?? ""}${trailing}`;

    return (
        <>
            {template.slice(0, match.index)}
            <Text as="button" type="button" {...suggestionLinkStyles} onClick={onClick}>
                {label}
            </Text>
            {after}
        </>
    );
};

export const EmptyResultSearchPage: FC<EmptyResultSearchPageProps> = ({
    query = "",
    trendingRecommendations,
    customLinksCarousel,
    showQueryFeedback = true,
}) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { siteSettings } = useGlobalContent();
    const locale = useLocale();
    const { push } = useRouter();
    const suggestion = useDidYouMeanSuggestion(showQueryFeedback ? query : "");
    const isSmUp = useSmBreakpoint();

    const theme = siteSettings.promotions.theme ?? "";
    const searchPagePath = getPagePathByLocale(searchPage, locale);
    const didYouMeanTemplate = getMicrocopy(CommonCMS.page, SearchCMS.noResultsDidYouMean);

    return (
        <Flex direction="column" w="100%" maxW="100%" minW={0} overflowX="hidden">
            {showQueryFeedback ? (
                <>
                    <H as="h1" size="h2">
                        {getMicrocopy(CommonCMS.page, SearchCMS.noResultsHeadline, {
                            replacements: { query },
                        })}
                    </H>
                    {suggestion && didYouMeanTemplate ? (
                        <H as="p" size="h3" {...didYouMeanHeadingStyles}>
                            {renderDidYouMean(didYouMeanTemplate, suggestion, () =>
                                push(
                                    `${searchPagePath}?query=${encodeURIComponent(suggestion.term)}`
                                )
                            )}
                        </H>
                    ) : null}
                </>
            ) : null}
            {customLinksCarousel?.length ? (
                <Box mt={showQueryFeedback ? [8, 10] : 0} w="100%" minW={0}>
                    <SearchCarousels
                        customLinksCarousel={customLinksCarousel}
                        variant="categories"
                    />
                </Box>
            ) : null}
            {isSmUp ? (
                <Box
                    mt={10}
                    w="100vw"
                    ml={["-layoutMargin.mobile", "-layoutMargin.tab", "-layoutMargin.desktop"]}
                    maxW="100vw"
                >
                    <ProductRecommendations
                        theme={theme}
                        headline={
                            getMicrocopy(CommonCMS.page, SearchCMS.trendingItemsHeadline) ??
                            "Trending items"
                        }
                        recommendations={trendingRecommendations}
                        carouselHeight={["auto", "100vw", "68vw", "52vw", "49vw", "900px"]}
                        tileWidth={[
                            ...PRODUCT_CAROUSEL_DEFAULT_TILE_WIDTHS,
                            "calc(100vw/3.5)",
                            "550px",
                        ]}
                        model={AlgoliaRecommendModel.TRENDING_ITEMS}
                        disableItemMinWidth
                    />
                </Box>
            ) : null}
        </Flex>
    );
};
